// Settings page - Mobile parity version
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost, apiPatch, API_BASE_URL } from "../lib/api";
import { useAuth } from "../context/AuthProvider";

interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  showEmail?: boolean;
  isAdmin?: boolean;
}

export default function Settings() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // Password change states
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Email change states
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  
  // Personal info states
  const [showPersonalForm, setShowPersonalForm] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [gender, setGender] = useState("");
  
  // Privacy states
  const [showEmailPublic, setShowEmailPublic] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const response = await apiGet<{ ok: true; user: User }>("/api/profile/me");
      setCurrentUser(response.user);
      setFirstName(response.user.firstName || "");
      setLastName(response.user.lastName || "");
      setPhoneNumber(response.user.phoneNumber || "");
      setGender(response.user.gender || "");
      setShowEmailPublic(response.user.showEmail || false);
      
      if (response.user.dateOfBirth) {
        const date = new Date(response.user.dateOfBirth);
        setBirthDay(date.getDate().toString());
        setBirthMonth((date.getMonth() + 1).toString());
        setBirthYear(date.getFullYear().toString());
      }
    } catch (error) {
      console.error("Failed to load user data:", error);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      alert("Lütfen tüm alanları doldurun");
      return;
    }
    if (newPassword !== confirmPassword) {
      alert("Yeni şifreler eşleşmiyor");
      return;
    }
    if (newPassword.length < 6) {
      alert("Yeni şifre en az 6 karakter olmalı");
      return;
    }

    setLoading(true);
    try {
      await apiPost("/api/auth/change-password", {
        oldPassword,
        newPassword,
      });
      alert("Şifreniz değiştirildi");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordForm(false);
    } catch (error: any) {
      alert(error?.message || "Şifre değiştirilemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail || !emailPassword) {
      alert("Lütfen yeni e-posta ve şifrenizi girin");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      alert("Geçerli bir e-posta adresi girin");
      return;
    }

    if (newEmail === currentUser?.email) {
      alert("Yeni e-posta adresi mevcut adresinizle aynı olamaz");
      return;
    }

    setLoading(true);
    try {
      await apiPost("/api/auth/change-email", {
        newEmail,
        password: emailPassword,
      });
      alert("E-posta adresiniz güncellendi");
      setShowEmailForm(false);
      setNewEmail("");
      setEmailPassword("");
      await loadUserData();
      await refreshMe();
    } catch (error: any) {
      // "Şifre yanlış" mesajını gösterme
      const errorMsg = error?.message || "";
      if (errorMsg.toLowerCase().includes('şifre')) {
        alert("E-posta güncellenirken hata oluştu");
      } else {
        alert(errorMsg || "E-posta güncellenirken hata oluştu");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePersonalInfo = async () => {
    // Name validation
    if (firstName && firstName.trim()) {
      const nameRegex = /^[a-zA-ZğüşıöçĞÜŞİÖÇ\s]+$/;
      if (!nameRegex.test(firstName.trim())) {
        alert("Ad alanına sadece harf girebilirsiniz.\nSayı veya noktalama işareti kullanılamaz.");
        return;
      }
    }

    if (lastName && lastName.trim()) {
      const nameRegex = /^[a-zA-ZğüşıöçĞÜŞİÖÇ\s]+$/;
      if (!nameRegex.test(lastName.trim())) {
        alert("Soyad alanına sadece harf girebilirsiniz.\nSayı veya noktalama işareti kullanılamaz.");
        return;
      }
    }

    // Phone number validation
    if (phoneNumber && phoneNumber.trim()) {
      const cleanPhone = phoneNumber.replace(/\s/g, "").replace(/[\-\(\)]/g, "");
      const phoneRegex = /^[\+]?[0-9]{10,15}$/;
      
      if (!phoneRegex.test(cleanPhone)) {
        alert("Lütfen geçerli bir telefon numarası girin.\n\nÖrnekler:\n• Türkiye: 0532 123 45 67\n• ABD: +1 234 567 8900\n• İngiltere: +44 20 1234 5678\n\nTelefon numarası 10-15 rakam içermelidir.");
        return;
      }
    }

    // Date validation
    if (birthDay || birthMonth || birthYear) {
      const day = parseInt(birthDay);
      const month = parseInt(birthMonth);
      const year = parseInt(birthYear);

      if (!day || !month || !year || day < 1 || day > 31 || month < 1 || month > 12) {
        alert("Geçerli bir doğum tarihi girin");
        return;
      }

      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear - 10) {
        alert("Geçerli bir doğum yılı girin");
        return;
      }
    }

    setLoading(true);
    try {
      let dateOfBirth = null;
      if (birthDay && birthMonth && birthYear) {
        const day = birthDay.padStart(2, "0");
        const month = birthMonth.padStart(2, "0");
        dateOfBirth = `${birthYear}-${month}-${day}`;
      }

      await apiPatch("/api/profile/personal-info", {
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        phoneNumber: phoneNumber.trim() || null,
        dateOfBirth,
        gender: gender || null,
      });

      alert("Kişisel bilgileriniz güncellendi");
      setShowPersonalForm(false);
      await loadUserData();
      await refreshMe();
    } catch (error: any) {
      alert(error?.message || "Bilgiler güncellenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrivacy = async (showEmail: boolean) => {
    setLoading(true);
    try {
      await apiPatch("/api/profile/privacy", {
        showEmail,
      });
      setShowEmailPublic(showEmail);
      await loadUserData();
      await refreshMe();
    } catch (error: any) {
      alert(error?.message || "Ayarlar güncellenirken hata oluştu");
      setShowEmailPublic(!showEmail);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div style={{ 
        padding: 24, 
        minHeight: "100vh", 
        backgroundColor: "#000", 
        color: "#fff" 
      }}>
        Yükleniyor...
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: "#000",
      color: "#fff",
      padding: 16,
    }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        
        {/* Profil Yönetimi */}
        <section style={{ marginBottom: 24 }}>
          <h2 style={{
            color: "#114232",
            fontSize: 18,
            fontWeight: 800,
            marginBottom: 12,
          }}>
            Profil
          </h2>
          
          <button
            onClick={() => navigate(`/profile/${currentUser.id}`)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              backgroundColor: "#1a1a1a",
              border: "1px solid #114232",
              borderRadius: 12,
              padding: 16,
              marginBottom: 8,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 20 }}>👤</span>
              <span>Profilimi Görüntüle</span>
            </div>
            <span style={{ color: "#114232", fontSize: 20 }}>→</span>
          </button>

          <button
            onClick={() => navigate("/profile/edit")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              backgroundColor: "#1a1a1a",
              border: "1px solid #114232",
              borderRadius: 12,
              padding: 16,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 20 }}>✏️</span>
              <span>Profili Düzenle</span>
            </div>
            <span style={{ color: "#114232", fontSize: 20 }}>→</span>
          </button>
        </section>

        {/* Hesap Ayarları */}
        <section style={{ marginBottom: 24 }}>
          <h2 style={{
            color: "#114232",
            fontSize: 18,
            fontWeight: 800,
            marginBottom: 12,
          }}>
            Hesap Ayarları
          </h2>

          {/* E-posta Değiştirme */}
          <div style={{
            backgroundColor: "#1a1a1a",
            borderRadius: 8,
            padding: 16,
            marginBottom: 8,
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                <span style={{ fontSize: 24, color: "#007AFF" }}>📧</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>E-posta</div>
                  <div style={{ fontSize: 13, color: "#999" }}>{currentUser.email}</div>
                </div>
              </div>
              {!showEmailForm && (
                <button
                  onClick={() => setShowEmailForm(true)}
                  style={{
                    backgroundColor: "#114232",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 16px",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Değiştir
                </button>
              )}
            </div>

            {showEmailForm && (
              <div style={{
                backgroundColor: "#2a2a2a",
                borderRadius: 8,
                padding: 16,
                marginTop: 16,
              }}>
                <input
                  type="email"
                  placeholder="Yeni E-posta"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  style={{
                    width: "100%",
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #333",
                    borderRadius: 8,
                    padding: 12,
                    color: "#fff",
                    fontSize: 16,
                    marginBottom: 8,
                  }}
                />
                <input
                  type="password"
                  placeholder="Mevcut Şifre"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  style={{
                    width: "100%",
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #333",
                    borderRadius: 8,
                    padding: 12,
                    color: "#fff",
                    fontSize: 16,
                    marginBottom: 16,
                  }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => {
                      setShowEmailForm(false);
                      setNewEmail("");
                      setEmailPassword("");
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "transparent",
                      border: "1px solid #666",
                      borderRadius: 8,
                      padding: 12,
                      color: "#ccc",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleChangeEmail}
                    disabled={loading}
                    style={{
                      flex: 1,
                      backgroundColor: "#114232",
                      border: "none",
                      borderRadius: 8,
                      padding: 12,
                      color: "#fff",
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: loading ? "not-allowed" : "pointer",
                      opacity: loading ? 0.5 : 1,
                    }}
                  >
                    {loading ? "Güncelleniyor..." : "Güncelle"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Kişisel Bilgiler */}
          <div style={{
            backgroundColor: "#1a1a1a",
            borderRadius: 8,
            padding: 16,
            marginTop: 16,
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                <span style={{ fontSize: 24, color: "#007AFF" }}>👤</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Kişisel Bilgiler</div>
                  {(currentUser.firstName || currentUser.lastName) && (
                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                      {currentUser.firstName} {currentUser.lastName}
                    </div>
                  )}
                  {currentUser.phoneNumber && (
                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                      Tel: {currentUser.phoneNumber}
                    </div>
                  )}
                  {currentUser.dateOfBirth && (
                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                      {(() => {
                        const date = new Date(currentUser.dateOfBirth);
                        const day = date.getDate().toString().padStart(2, "0");
                        const month = (date.getMonth() + 1).toString().padStart(2, "0");
                        const year = date.getFullYear();
                        return `${day}.${month}.${year}`;
                      })()}
                    </div>
                  )}
                  {currentUser.gender && (
                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                      {currentUser.gender === "male" ? "Erkek" : currentUser.gender === "female" ? "Kadın" : "Diğer"}
                    </div>
                  )}
                </div>
              </div>
              {!showPersonalForm && (
                <button
                  onClick={() => setShowPersonalForm(true)}
                  style={{
                    backgroundColor: "#114232",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 16px",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Düzenle
                </button>
              )}
            </div>

            {showPersonalForm && (
              <div style={{
                backgroundColor: "#2a2a2a",
                borderRadius: 8,
                padding: 16,
                marginTop: 16,
              }}>
                <input
                  type="text"
                  placeholder="Ad"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #333",
                    borderRadius: 8,
                    padding: 12,
                    color: "#fff",
                    fontSize: 16,
                    marginBottom: 8,
                  }}
                />
                <input
                  type="text"
                  placeholder="Soyad"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #333",
                    borderRadius: 8,
                    padding: 12,
                    color: "#fff",
                    fontSize: 16,
                    marginBottom: 8,
                  }}
                />
                
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 12, marginBottom: 8 }}>
                  Telefon Numarası
                </div>
                <div style={{ fontSize: 12, color: "#888", lineHeight: 1.5, marginBottom: 8 }}>
                  Ülke kodunuz ile birlikte yazın<br />
                  🇹🇷 Türkiye: +90 532 123 45 67 veya 0532 123 45 67<br />
                  🌍 Diğer: +1 234 567 8900 (ABD), +44 20 1234 5678 (UK)
                </div>
                <input
                  type="tel"
                  placeholder="+90 5xx xxx xx xx"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #333",
                    borderRadius: 8,
                    padding: 12,
                    color: "#fff",
                    fontSize: 16,
                    marginBottom: 8,
                  }}
                />
                
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 12, marginBottom: 8 }}>
                  Doğum Tarihi
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <input
                    type="number"
                    placeholder="Gün"
                    value={birthDay}
                    onChange={(e) => setBirthDay(e.target.value)}
                    maxLength={2}
                    style={{
                      flex: 1,
                      boxSizing: "border-box",
                      backgroundColor: "#0a0a0a",
                      border: "1px solid #333",
                      borderRadius: 8,
                      padding: 12,
                      color: "#fff",
                      fontSize: 16,
                      textAlign: "center",
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Ay"
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(e.target.value)}
                    maxLength={2}
                    style={{
                      flex: 1,
                      boxSizing: "border-box",
                      backgroundColor: "#0a0a0a",
                      border: "1px solid #333",
                      borderRadius: 8,
                      padding: 12,
                      color: "#fff",
                      fontSize: 16,
                      textAlign: "center",
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Yıl"
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                    maxLength={4}
                    style={{
                      flex: 1,
                      boxSizing: "border-box",
                      backgroundColor: "#0a0a0a",
                      border: "1px solid #333",
                      borderRadius: 8,
                      padding: 12,
                      color: "#fff",
                      fontSize: 16,
                      textAlign: "center",
                    }}
                  />
                </div>

                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
                  Cinsiyet
                </div>
                <div style={{ marginBottom: 16 }}>
                  {["male", "female", "other"].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGender(g)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        backgroundColor: gender === g ? "#114232" : "transparent",
                        border: "1px solid #666",
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 8,
                        color: gender === g ? "#fff" : "#ccc",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      <div style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        border: "2px solid #666",
                        marginRight: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: gender === g ? "#114232" : "transparent",
                      }}>
                        {gender === g && <span style={{ color: "#fff", fontSize: 18 }}>✓</span>}
                      </div>
                      {g === "male" ? "Erkek" : g === "female" ? "Kadın" : "Diğer"}
                    </button>
                  ))}
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => {
                      setShowPersonalForm(false);
                      setFirstName(currentUser.firstName || "");
                      setLastName(currentUser.lastName || "");
                      setPhoneNumber(currentUser.phoneNumber || "");
                      setGender(currentUser.gender || "");
                      if (currentUser.dateOfBirth) {
                        const date = new Date(currentUser.dateOfBirth);
                        setBirthDay(date.getDate().toString());
                        setBirthMonth((date.getMonth() + 1).toString());
                        setBirthYear(date.getFullYear().toString());
                      } else {
                        setBirthDay("");
                        setBirthMonth("");
                        setBirthYear("");
                      }
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: "transparent",
                      border: "1px solid #666",
                      borderRadius: 8,
                      padding: 12,
                      color: "#ccc",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleUpdatePersonalInfo}
                    disabled={loading}
                    style={{
                      flex: 1,
                      backgroundColor: "#114232",
                      border: "none",
                      borderRadius: 8,
                      padding: 12,
                      color: "#fff",
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: loading ? "not-allowed" : "pointer",
                      opacity: loading ? 0.5 : 1,
                    }}
                  >
                    {loading ? "Güncelleniyor..." : "Güncelle"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Gizlilik */}
        <section style={{ marginBottom: 24 }}>
          <h2 style={{
            color: "#114232",
            fontSize: 18,
            fontWeight: 800,
            marginBottom: 12,
          }}>
            Gizlilik
          </h2>

          <div style={{
            backgroundColor: "#1a1a1a",
            borderRadius: 8,
            padding: 16,
            marginBottom: 12,
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                <span style={{ fontSize: 24, color: "#007AFF" }}>📧</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                    E-posta Adresimi Göster
                  </div>
                  <div style={{ fontSize: 11, color: "#888", lineHeight: 1.4 }}>
                    {showEmailPublic 
                      ? "✅ Diğer kullanıcılar e-postanızı görebilir" 
                      : "🔒 E-postanız tüm kullanıcılara kapalı"}
                  </div>
                </div>
              </div>
              <label style={{ position: "relative", display: "inline-block", width: 51, height: 31 }}>
                <input
                  type="checkbox"
                  checked={showEmailPublic}
                  onChange={(e) => handleUpdatePrivacy(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: "absolute",
                  cursor: "pointer",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: showEmailPublic ? "#114232" : "#767577",
                  transition: "0.4s",
                  borderRadius: 34,
                }}>
                  <span style={{
                    position: "absolute",
                    content: "",
                    height: 26,
                    width: 26,
                    left: showEmailPublic ? 26 : 4,
                    bottom: 2.5,
                    backgroundColor: showEmailPublic ? "#1a5a44" : "#f4f3f4",
                    transition: "0.4s",
                    borderRadius: "50%",
                  }} />
                </span>
              </label>
            </div>

            <div style={{ fontSize: 11, color: "#888", marginTop: 8, marginLeft: 8 }}>
              💡 Kişisel bilgileriniz (ad, soyad, telefon, doğum tarihi) her zaman gizlidir.
            </div>
          </div>

          <button
            onClick={() => navigate("/settings/blocked")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              backgroundColor: "#1a1a1a",
              border: "1px solid #114232",
              borderRadius: 12,
              padding: 16,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 20 }}>🚫</span>
              <span>Engellenen Kullanıcılar</span>
            </div>
            <span style={{ color: "#114232", fontSize: 20 }}>→</span>
          </button>
        </section>

        {/* Güvenlik */}
        <section style={{ marginBottom: 24 }}>
          <h2 style={{
            color: "#114232",
            fontSize: 18,
            fontWeight: 800,
            marginBottom: 12,
          }}>
            Güvenlik
          </h2>

          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              backgroundColor: "#1a1a1a",
              border: "1px solid #114232",
              borderRadius: 12,
              padding: 16,
              marginBottom: 8,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 20 }}>🔑</span>
              <span>Şifre Değiştir</span>
            </div>
            <span style={{ color: "#114232", fontSize: 20 }}>
              {showPasswordForm ? "▼" : "→"}
            </span>
          </button>

          {showPasswordForm && (
            <div style={{
              backgroundColor: "#1a1a1a",
              border: "1px solid #114232",
              borderRadius: 12,
              padding: 16,
              marginTop: 12,
            }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#ccc", display: "block", marginBottom: 8 }}>
                  Eski Şifre
                </label>
                <input
                  type="password"
                  placeholder="Eski şifrenizi girin"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  disabled={loading}
                  style={{
                    width: "100%",
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #333",
                    borderRadius: 8,
                    padding: 12,
                    color: "#fff",
                    fontSize: 16,
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#ccc", display: "block", marginBottom: 8 }}>
                  Yeni Şifre
                </label>
                <input
                  type="password"
                  placeholder="Yeni şifrenizi girin"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  style={{
                    width: "100%",
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #333",
                    borderRadius: 8,
                    padding: 12,
                    color: "#fff",
                    fontSize: 16,
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#ccc", display: "block", marginBottom: 8 }}>
                  Yeni Şifre Tekrarı
                </label>
                <input
                  type="password"
                  placeholder="Yeni şifrenizi tekrar girin"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  style={{
                    width: "100%",
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #333",
                    borderRadius: 8,
                    padding: 12,
                    color: "#fff",
                    fontSize: 16,
                  }}
                />
              </div>

              <button
                onClick={handleChangePassword}
                disabled={loading}
                style={{
                  width: "100%",
                  backgroundColor: "#114232",
                  border: "none",
                  borderRadius: 8,
                  padding: 14,
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                  marginBottom: 8,
                }}
              >
                {loading ? "Değiştiriliyor..." : "Şifreyi Değiştir"}
              </button>

              <button
                onClick={() => {
                  setShowPasswordForm(false);
                  setOldPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                disabled={loading}
                style={{
                  width: "100%",
                  backgroundColor: "transparent",
                  border: "1px solid #666",
                  borderRadius: 8,
                  padding: 14,
                  color: "#ccc",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                İptal
              </button>
            </div>
          )}
        </section>

        {/* Admin Panel */}
        {currentUser.isAdmin && (
          <section style={{ marginBottom: 24 }}>
            <h2 style={{
              color: "#114232",
              fontSize: 18,
              fontWeight: 800,
              marginBottom: 12,
            }}>
              Admin
            </h2>

            <button
              onClick={() => navigate("/admin/dashboard")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(59, 130, 246, 0.1)",
                border: "2px solid rgba(59, 130, 246, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#3b82f6",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>📊</span>
                <span>Dashboard</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/reports")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "2px solid rgba(239, 68, 68, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#ef4444",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>📋</span>
                <span>Raporlar</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/announcements")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(251, 191, 36, 0.1)",
                border: "2px solid rgba(251, 191, 36, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#fbbf24",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>📢</span>
                <span>Duyurular</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/users-with-phone")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(139, 92, 246, 0.1)",
                border: "2px solid rgba(139, 92, 246, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#8b5cf6",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>📱</span>
                <span>Telefon Kayıtlı Kullanıcılar</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/usernames")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(59, 130, 246, 0.1)",
                border: "2px solid rgba(59, 130, 246, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#3b82f6",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20, color: "#FFD700" }}>🛡️</span>
                <span>Admin Panel</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/audit")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(139, 92, 246, 0.1)",
                border: "2px solid rgba(139, 92, 246, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#8b5cf6",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>📋</span>
                <span>Audit Logs</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/savwar")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(16, 185, 129, 0.1)",
                border: "2px solid rgba(16, 185, 129, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#10b981",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>⚔️</span>
                <span>SavWar Yönetimi</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/savkabile")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(212, 175, 55, 0.1)",
                border: "2px solid rgba(212, 175, 55, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#d4af37",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>🏛️</span>
                <span>SavKabile Yönetimi</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/security")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "2px solid rgba(239, 68, 68, 0.4)",
                borderRadius: 12,
                padding: 16,
                color: "#ef4444",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>🛡️</span>
                <span>Security Dashboard</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/login-attempts")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(249, 115, 22, 0.1)",
                border: "2px solid rgba(249, 115, 22, 0.4)",
                borderRadius: 12,
                padding: 16,
                color: "#f97316",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>🔐</span>
                <span>Login Attempts / Failed Logins</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/notification-rules")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(139, 92, 246, 0.1)",
                border: "2px solid rgba(139, 92, 246, 0.4)",
                borderRadius: 12,
                padding: 16,
                color: "#8b5cf6",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>🔔</span>
                <span>Notification Rules & System Alerts</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/premium")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(168, 85, 247, 0.1)",
                border: "2px solid rgba(168, 85, 247, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#a855f7",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>💎</span>
                <span>Premium Items Yönetimi</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/outbox")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(20, 184, 166, 0.1)",
                border: "2px solid rgba(20, 184, 166, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#14b8a6",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>📨</span>
                <span>Outbox</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            <button
              onClick={() => navigate("/admin/ads")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "rgba(251, 146, 60, 0.1)",
                border: "2px solid rgba(251, 146, 60, 0.4)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 8,
                color: "#fb923c",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>📺</span>
                <span>Reklam Ayarları</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>

            {/* Performance sayfası henüz eklenmedi
            <button
              onClick={() => navigate("/performance-insights")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                backgroundColor: "#1a1a1a",
                border: "1px solid #114232",
                borderRadius: 12,
                padding: 16,
                color: "#fff",
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 20 }}>📊</span>
                <span>Performans İstatistikleri</span>
              </div>
              <span style={{ color: "#114232", fontSize: 20 }}>→</span>
            </button>
            */}
          </section>
        )}

        {/* Yasal ve Hakkında */}
        <section style={{ marginBottom: 24 }}>
          <h2 style={{
            color: "#114232",
            fontSize: 18,
            fontWeight: 800,
            marginBottom: 12,
          }}>
            Yasal
          </h2>

          <button
            onClick={() => navigate("/terms")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              backgroundColor: "#1a1a1a",
              border: "1px solid #114232",
              borderRadius: 12,
              padding: 16,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 20 }}>📜</span>
              <span>Kullanıcı Sözleşmesi</span>
            </div>
            <span style={{ color: "#114232", fontSize: 20 }}>→</span>
          </button>
        </section>

        {/* Çıkış */}
        <section style={{ marginBottom: 24 }}>
          <button
            onClick={logout}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              backgroundColor: "#3a0a0a",
              border: "2px solid #5a1a1a",
              borderRadius: 12,
              padding: 16,
              gap: 8,
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 20, color: "#FF3B30" }}>🚪</span>
            <span style={{ color: "#ff6b6b", fontSize: 16, fontWeight: 700 }}>
              Hesaptan Çıkış Yap
            </span>
          </button>
        </section>
      </div>
    </div>
  );
}
